import React, { useCallback, useState, useMemo, useEffect } from 'react';
import { Modal, Form, Button, Message, Icon, Dimmer, Loader, Segment } from 'semantic-ui-react';
import DatePicker from '../../DatePicker';
import PaymentSourceDisplay from '../../../external-components/payments/steps/PaymentSourceDisplay';
import IntlCurrencyInput from 'react-intl-currency-input';
import moment from 'moment';
import numeral from 'numeral';
import styles from './AddScheduledPaymentModal.module.scss';
import orderBy from 'lodash.orderby';
import WalletWarningModal from '../../../external-components/payments/steps/WalletWarningModal';

const AddScheduledPaymentModal = ({ open, onOpen, onClose, onAdd, wallets, policies, actions, scheduledPayments }) => {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [isFuture, setIsFuture] = useState(false);
	const [showApproval, setShowApproval] = useState(false);

	const minDate = useMemo(() => Date.parse(moment().add(1, 'days').format()), []);

	const options = useMemo(() => {
		return policies.filter(p => moment((p.futurePolicy || p).term.endDate) >= minDate && p.status === 'In-Force' && (!p.billing.isPartOfAccountBill || (p.billing.isPartOfAccountBill && p.billing.isPrimaryAccountPolicy))).map((p) => {
			const val = p.billing.isPrimaryAccountPolicy ? p.billing.accountNumber : p.policyNumber
			return { key: val, value: p.policyNumber, text: val };
		});
	}, [minDate, policies]);

	const methods = useMemo(() => {
		return [
			{ key: 'new', value: 'new', text: 'New Wallet Item' },
			...orderBy(
				wallets.map((w) => {
					return {
						key: w.fundingAccountToken,
						value: w.fundingAccountToken,
						text: w.nickname,
						wallet: w,
						content: (
							<div>
								<div className={styles.methodItemText}>{w.nickname}</div>
								<div>
									<PaymentSourceDisplay source={{ ...w, fiserv: w }} />
								</div>
							</div>
						),
					};
				}),
				(w) => w.nickname
			),
		];
	}, [wallets]);

	const [data, setData] = useState({
		date: minDate,
		method: methods[1].value,
		policyNumber: options[0].value,
		amount: 10,
	});

	const selectedPolicy = useMemo(() => {
		return policies.find((p) => p.policyNumber === data.policyNumber || p.billing.accountNumber === data.policyNumber);
	}, [data.policyNumber, policies]);
	const selectedWallet = useMemo(() => methods.find((m) => m.value === data.method).wallet, [data.method, methods])

	const maxDate = useMemo(() => {
		return Date.parse(moment((selectedPolicy.futurePolicy || selectedPolicy).term.endDate).format());
	}, [selectedPolicy]);

	const amount = useMemo(() => {
		const { paymentInfo } = isFuture && selectedPolicy.futurePolicy ? selectedPolicy.futurePolicy : selectedPolicy;

		switch (data.paymentOption) {
			case 'currentBalance':
				return paymentInfo.isPartOfAccountBill ? paymentInfo.accountOutstandingBalance : paymentInfo.outstandingBalance;
			case 'totalBalance':
				return paymentInfo.isPartOfAccountBill ? paymentInfo.accountPayInFull : paymentInfo.payInFullAmount;
			default:
				return data.amount;
		}

	}, [data.amount, data.paymentOption, isFuture, selectedPolicy]);

	const [shouldCalculateConFee, setShouldCalculateConFee] = useState(false);
	const [conFee, setConFee] = useState('');
	const [enableAddButton, setEnableAddButton ] = useState(false);

	const isCreditCard = useMemo(() => selectedWallet.fundingCategory.toLowerCase() === 'credit', [selectedWallet]);
	const showConvFee = isCreditCard === true && shouldCalculateConFee === true;

	
	const shouldCalConFee = async () => {
		try {
			setLoading(true);

			const calculateFeeResponse = await actions.shouldCalculateConvienceFee(data.date);
			console.log(calculateFeeResponse);
			const { shouldCalculateFee } = calculateFeeResponse.response;
			setShouldCalculateConFee(shouldCalculateFee);

			if(shouldCalculateFee && amount >= 10) {
				convFeeLoad();
			}

			setLoading(false);
		} catch (err) {
			console.log(err)
			setLoading(false);
		}
		
	};

	
	const convFeeLoad = async () => {
		try {
			setLoading(true);
			setEnableAddButton(false);

				var policy = policies.find(p => p.policyNumber === data.policyNumber);
				var accountNumber = policy.billing.isPrimaryAccountPolicy ? policy.billing.accountNumber : null;
				const result = await actions.CalculateConvFeeScheduled(data.policyNumber, amount, selectedWallet.fundingAccountToken, selectedWallet.walletItemKeyIdentifier, accountNumber);
				console.log(result);
				const { responseData: { feeAmount, success } } = result.response;
				console.log(feeAmount, success)
				if (success) {
					setConFee(feeAmount)

				}
			setLoading(false);
		} catch (err) {
			console.log(err)
			setLoading(false);
		}
		setEnableAddButton(true);
	};

	useEffect(() => {
		if (selectedWallet.fundingCategory.toLowerCase() === 'credit' && new Date(data.date).getTime() >= (new Date()).getTime() ) {
			shouldCalConFee();
		}
		else {
			setConFee('');
		}


	}, [actions, data.date, selectedWallet, amount]);

	const paymentOptions = useMemo(() => {
		let { paymentInfo } = selectedPolicy;
		const options = [];

		if ((paymentInfo.isPartOfAccountBill && paymentInfo.accountOutstandingBalance > 0) || (!paymentInfo.isPartOfAccountBill && paymentInfo.outstandingBalance > 0)) {
			options.push({ key: 'currentBalance', value: 'currentBalance', text: 'Current Balance' });
		}

		if ((paymentInfo.isPartOfAccountBill && paymentInfo.accountPayInFull > 0) || (!paymentInfo.isPartOfAccountBill && paymentInfo.payInFullAmount > 0)) {
			options.push({ key: 'totalBalance', value: 'totalBalance', text: 'Total Balance' });
		}
		setIsFuture(false);

		if (options.length === 0 && selectedPolicy.futurePolicy) {
			paymentInfo = selectedPolicy.futurePolicy.paymentInfo;

			if ((paymentInfo.isPartOfAccountBill && paymentInfo.accountOutstandingBalance > 0) || (!paymentInfo.isPartOfAccountBill && paymentInfo.outstandingBalance > 0)) {
				options.push({ key: 'currentBalance', value: 'currentBalance', text: 'Current Balance' });
			}

			if ((paymentInfo.isPartOfAccountBill && paymentInfo.accountPayInFull > 0) || (!paymentInfo.isPartOfAccountBill && paymentInfo.payInFullAmount > 0)) {
				options.push({ key: 'totalBalance', value: 'totalBalance', text: 'Total Balance' });
			}

			setIsFuture(true);
		}

		options.push({ key: 'customPayment', value: 'customPayment', text: 'Custom Payment' });

		if (!options.find((o) => o.value === data.paymentOption)) {
			setData({ ...data, paymentOption: options[0].value });
		}

		return options;
	}, [data, selectedPolicy]);

	useEffect(() => {
		if (data.date > maxDate) {
			setData({ ...data, date: maxDate });
		}
	}, [data, data.policyNumber, maxDate]);

	const save = useCallback(async () => {
		setLoading(true);
		try {
			setError(false);
			var policy = policies.find(p => p.policyNumber === data.policyNumber);
			var accountNumber = policy.billing.isPrimaryAccountPolicy ? policy.billing.accountNumber : null;
			await actions.addScheduledPayment(data.policyNumber, amount, data.date, selectedWallet.fundingAccountToken, selectedWallet.walletItemId, selectedWallet.walletItemKeyIdentifier, accountNumber, conFee);
			onAdd();
		} catch(error) {
			setError(true);
		}
		setLoading(false);
	}, [actions, amount, data.date, data.policyNumber, onAdd, policies, selectedWallet, conFee]);

	const onApproved = useCallback(async () => {
		setShowApproval(false);
		await save();
	}, [save]);

	const onAddButtonClick = useCallback(async () => {
		if (scheduledPayments.some((sp) => sp.policyNumber === data.policyNumber && sp.fundingAccountToken === selectedWallet.fundingAccountToken && moment(sp.paymentScheduleDate).format() === moment(data.date).format())) {
			setShowApproval(true);
			return;
		}

		await save();
	}, [data.date, data.policyNumber, save, scheduledPayments, selectedWallet.fundingAccountToken]);

	const _onOpen = useCallback(() => {
		setError(null);
		if (onOpen) onOpen();
	}, [onOpen]);

	//const isValid = useMemo(() => {
	//	return data.date != null && data.date !== '' && data.method != null && data.method !== '' && data.policyNumber != null && data.policyNumber !== '' && amount >= 10;
	//}, [data.date, data.method, data.policyNumber, amount]);

	useEffect(() => {
		if (data.date != null && data.date !== '' && data.method != null && data.method !== '' && data.policyNumber != null && data.policyNumber !== '' && amount >= 10) {
			setEnableAddButton(true);
		}
		else {
			setEnableAddButton(false);
			setConFee('');
		}
	}, [data.date, data.method, data.policyNumber, amount]);

	const onChange = useCallback(
		(_, { name, value }) => {
			setData({ ...data, [name]: value });
		},
		[data]
	);

	const onPolicyNumberChange = useCallback(
		(_, { value }) => {
			setData({ ...data, policyNumber: value, date: minDate });
		},
		[data, minDate]
	);

	const onPaymentOptionChange = useCallback(
		(_, { value }) => {
			setData({ ...data, paymentOption: value, amount: value !== 'customPayment' ? 10 : amount });
		},
		[amount, data]
	);

	const [walletWarningOpen, setWalletWarningOpen] = useState(false);

	const onMethodChange = useCallback(
		(_, { value }) => {
			if (value === 'new') {
				setWalletWarningOpen(true);
				return;
			}

			setData({ ...data, method: value });
		},
		[data]
	);

	const selectedMethod = useMemo(() => {
		return { fiserv: methods.find((w) => w.value === data.method).wallet };
	}, [data.method, methods]);

	useEffect(() => {
		const { paymentInfo } = isFuture && selectedPolicy.futurePolicy ? selectedPolicy.futurePolicy : selectedPolicy;

		if ((paymentInfo.isPartOfAccountBill && paymentInfo.accountOutstandingBalance > 0) || (!paymentInfo.isPartOfAccountBill && paymentInfo.outstandingBalance > 0)) {
			setData({ ...data, paymentOption: 'currentBalance' });
		} else if ((paymentInfo.isPartOfAccountBill && paymentInfo.accountPayInFull > 0) || (!paymentInfo.isPartOfAccountBill && paymentInfo.payInFullAmount > 0)) {
			setData({ ...data, paymentOption: 'totalBalance' });
		} else {
			setData({ ...data, paymentOption: 'customPayment', amount: 10 });
		}
		
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isFuture, selectedPolicy]);

	return (
		<React.Fragment>
			<WalletWarningModal open={walletWarningOpen} onClose={() => setWalletWarningOpen(false)} />
			<Modal
				open={open}
				onOpen={_onOpen}
				onClose={onClose}
				//size='tiny'
				size='large'
				trigger={
					<span style={{ cursor: 'pointer' }}>
						<Icon name='add' />
						Add Scheduled Payment
					</span>
				}>
				<Modal.Header>Add Scheduled Payment</Modal.Header>
				<Modal.Content>
					{loading && (
						<Dimmer active inverted>
							<Loader active size='massive'>
								Submitting Scheduled Payment...
							</Loader>
						</Dimmer>
					)}
					{showApproval && (
						<Modal open={showApproval} onClose={() => setShowApproval(false)} size='tiny'>
							<Modal.Header>Duplicate Scheduled Payment</Modal.Header>
							<Modal.Content>A payment with a duplicate amount for the same scheduled date exists. Are you sure?</Modal.Content>
							<Modal.Actions>
								<Button onClick={() => setShowApproval(false)}>Cancel</Button>
								<Button primary onClick={onApproved} disabled={!enableAddButton}>
									Add
								</Button>
							</Modal.Actions>
						</Modal>
					)}
					{error && <Message error>Error saving Scheduled Payment</Message>}
					<Form>
                        <Form.Dropdown name='policyNumber' label='Policy/Account Number' value={data.policyNumber} onChange={onPolicyNumberChange} required scrolling options={options} />
						<Form.Field>
							<label>Payment Date</label>
							<DatePicker name='date' placeholderText='Payment Date' selected={data.date} onChange={onChange} minDate={minDate} maxDate={maxDate} type="date" />
						</Form.Field>
						<Form.Dropdown name='method' label='Method' value={data.method} onChange={onMethodChange} required scrolling options={methods} />
						<Segment>
							<PaymentSourceDisplay source={selectedMethod} />
						</Segment>
						{showConvFee &&
							<div className="inline required field"><label className={styles.requiredLbl}> <b className={styles.displayInline}>Using a credit card now includes a convenience fee charged by our 3rd party credit card processor. The fee will be included in your total payment. To avoid paying this fee we also accept ACH/eCheck payments from your bank account.</b>
							</label>
							</div>
						}
						<Form.Dropdown name='paymentOption' label='Payment Option' value={data.paymentOption} onChange={onPaymentOptionChange} required scrolling options={paymentOptions} />
						<Form.Field control={IntlCurrencyInput} name='amount' label='Amount' value={amount} error={amount < 10 ? 'Amount must be at least $10.00' : null} onChange={(e, value) => onChange(e, { name: 'amount', value: value })} prefix='$ ' disabled={data.paymentOption !== 'customPayment'} />
						{showConvFee &&
							<Form.Field control={IntlCurrencyInput} name='conFee' label='Convenience Fee' value={numeral(conFee).format('$ 0,0.00') } prefix='$ ' disabled={true} />}
						{showConvFee && <Form.Field control={IntlCurrencyInput} name='totAmount' label='Total' value={numeral(Number(amount) + Number(conFee)).format('$ 0,0.00')} prefix='$ ' disabled={true} />}
						
					</Form>
				</Modal.Content>
				<Modal.Actions>
					<Button onClick={onClose}>Cancel</Button>
					<Button primary onClick={onAddButtonClick} disabled={!enableAddButton}>
						Add
					</Button>
				</Modal.Actions>
			</Modal>
		</React.Fragment>
	);
};

AddScheduledPaymentModal.propTypes = {};

export default AddScheduledPaymentModal;
